import React, { useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DOC_CATEGORIES } from "@/data/site";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const COMMON_DATA = ["financing_type", "property_type", "purchase_price", "current_value", "arv", "rehab_budget",
  "loan_amount", "monthly_rent", "closing_date", "borrowing_entity", "date_of_birth", "credit_range",
  "liquidity", "completed_deals", "rentals_owned"];
const TOP = [["applicant_name", "Borrower Name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"],
  ["property_address", "Property Address"], ["property_state", "Property State"]];
const inputCls = "bg-surface/70 border-edge text-white h-10 mt-1";
const label = (k) => k.replace(/_/g, " ");

export function EditDealDialog({ type, sub, onClose, onSaved }) {
  const [top, setTop] = useState(() => Object.fromEntries(TOP.map(([k]) => [k, sub[k] || ""])));
  const keys = Array.from(new Set([...COMMON_DATA, ...Object.keys(sub.data || {})]));
  const [data, setData] = useState(() => Object.fromEntries(keys.map((k) => [k, sub.data?.[k] ?? ""])));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const changed = Object.fromEntries(Object.entries(data).filter(([k, v]) => String(v) !== String(sub.data?.[k] ?? "")));
      await api.put(`/admin/submissions/${type}/${sub.id}`, { ...top, email: top.email || null, data: changed });
      toast.success("Deal details saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-charcoal border-edge text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-deal-dialog">
        <h2 className="h-display text-2xl text-white">Edit Deal Details</h2>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {TOP.map(([k, l]) => (
            <label key={k} className="text-xs text-faint">{l}
              <Input value={top[k]} onChange={(e) => setTop((s) => ({ ...s, [k]: e.target.value }))} className={inputCls} data-testid={`edit-deal-${k}`} />
            </label>
          ))}
          {keys.map((k) => (
            <label key={k} className="text-xs text-faint capitalize">{label(k)}
              <Input type={k.endsWith("date") || k === "date_of_birth" ? "date" : "text"} value={data[k]}
                onChange={(e) => setData((s) => ({ ...s, [k]: e.target.value }))} className={inputCls} data-testid={`edit-deal-data-${k}`} />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} className="text-white/70" data-testid="edit-deal-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="btn-gold rounded-lg" data-testid="edit-deal-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StaffUpload({ type, subId, onDone }) {
  const [cat, setCat] = useState(DOC_CATEGORIES[0]);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", cat);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const fd2 = new FormData();
        fd2.append("doc_id", data.id);
        await api.post(`/admin/submissions/${type}/${subId}/documents`, fd2);
      }
      toast.success(`${files.length} document(s) added`);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2 mt-3" data-testid="staff-upload">
      <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-md bg-surface/70 border border-edge text-white px-3 text-sm" data-testid="staff-upload-category">
        {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <label className={`btn-outline-glass rounded-lg h-10 px-4 inline-flex items-center gap-2 text-sm cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload for borrower
        <input type="file" multiple className="hidden" onChange={upload} data-testid="staff-upload-input" />
      </label>
    </div>
  );
}
